/**
 * Responsibility service, backed by Firestore (`responsibilities` collection).
 */

import { collection, doc, getDocs, setDoc, query, where } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../../../shared/firebase';

/**
 * Get all responsibilities
 * @param {Object} filters - Filter options (roleId, isUniversal)
 * @returns {Promise<Array>} Array of responsibilities
 */
export const getAllResponsibilities = async (filters = {}) => {
  try {
    let q = collection(db, 'responsibilities');
    const clauses = [];
    if (filters.roleId) clauses.push(where('roleId', '==', filters.roleId));
    if (filters.isUniversal !== undefined) {
      clauses.push(where('isUniversal', '==', !!filters.isUniversal));
    }
    if (clauses.length) q = query(q, ...clauses);
    const snap = await getDocs(q);
    const results = [];
    snap.forEach((d) => results.push(d.data()));
    return results;
  } catch (error) {
    console.error('Error fetching responsibilities:', error);
    throw error;
  }
};

/**
 * Get responsibilities by role
 * @returns {Promise<Array>} Array of responsibilities
 */
export const getResponsibilitiesByRole = async (roleId) => {
  return getAllResponsibilities({ roleId });
};

/**
 * Get universal responsibilities
 * @returns {Promise<Array>} Array of universal responsibilities
 */
export const getUniversalResponsibilities = async () => {
  return getAllResponsibilities({ isUniversal: true });
};

/**
 * Create a custom responsibility
 * @returns {Promise<Object>} Created responsibility object
 */
export const createResponsibility = async (responsibilityName) => {
  try {
    const responsibilityId = uuidv4();
    const data = {
      responsibilityId,
      responsibilityName,
      definition: null,
      isUniversal: false,
      roleId: null,
      frameworks: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await setDoc(doc(db, 'responsibilities', responsibilityId), data);
    return data;
  } catch (error) {
    console.error('Error creating responsibility:', error);
    throw error;
  }
};
